import { supervisedAssuranceCancel } from '@api/selfServiceServices';
import EpcsStartImg from '@assets/side/epcs_setup.svg';
import EpcsVerifyIdImg from '@assets/side/epcs_verify_id.svg';
import EpcsVerifyIidImg from '@assets/side/epcs_verify_iid.svg';
import { useSession } from '@common/authorization/context/Session';
import AuthMethodLayout from '@selfservice/containers/AuthMethods/AuthMethodLayout';
import type { PKCECodes } from '@selfservice/utils/pkce';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigateKeepSearch } from '@hooks/useNavigateKeepSearch';
import { v4 as uuidv4 } from 'uuid';
import { RootRoutes } from '@/index';
import EpcsStep1Start from './steps/EpcsStep1Start';
import EpcsStep2VerifyIdentity from './steps/EpcsStep2VerifyIdentity';
import EpcsStep3VerifyAuthenticator from './steps/EpcsStep3VerifyAuthenticator';
import EpcsStep4SupervisorAuthenticate from './steps/EpcsStep4SupervisorAuthenticate';
import './EpcsSetup.less';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useIdleTimer } from 'react-idle-timer';

const EPCS_IDLE_TIMEOUT_MS = 30_000;

export enum EpcsSetupStep {
  STEP1_START = 'step1-start',
  STEP2_VERIFY_IDENTITY = 'step2-verify-identity',
  STEP3_VERIFY_AUTHENTICATOR = 'step3-verify-authenticator',
  STEP4_SUPERVISOR_AUTHENTICATE = 'step4-supervisor-authenticate',
}

const TRANSLATION = 'self-service.epcs-setup';

const EpcsSetup = () => {
  const { tenantId } = useSession();
  const location = useLocation();
  const { t } = useTranslation();
  const { authenticatorId, authenticatorShortName, authenticatorDeviceModel } = location.state || {};
  const assuranceIdRef = useRef('');
  const workflowIdRef = useRef('');
  const [pkceCodes, setPKCECodes] = useState<PKCECodes>({ codeVerifier: '', codeChallenge: '' });
  const [currentStep, setCurrentStep] = useState<EpcsSetupStep>(EpcsSetupStep.STEP1_START);

  console.info('assuranceIdRef', assuranceIdRef.current);

  const navigateKeepSearch = useNavigateKeepSearch();

  const cancel = useCallback(() => {
    if (assuranceIdRef.current) {
      void supervisedAssuranceCancel(
        { assuranceId: assuranceIdRef.current },
        workflowIdRef.current,
        tenantId,
      );
      assuranceIdRef.current = '';
    }
  }, [tenantId]);

  const cancelFlow = useCallback(() => {
    navigateKeepSearch(RootRoutes.SELF_SERVICE);
  }, [navigateKeepSearch]);

  useIdleTimer({ onIdle: cancelFlow, timeout: EPCS_IDLE_TIMEOUT_MS });

  // On user navigating back, page refresh, timeout, etc.
  useEffect(() => {
    if (!authenticatorId) {
      cancelFlow();
      return;
    }
    workflowIdRef.current = uuidv4();
    window.addEventListener('beforeunload', cancel);
    return () => {
      window.removeEventListener('beforeunload', cancel);
      cancel();
    };
  }, [authenticatorId, cancel, cancelFlow]);

  const completeFlow = useCallback(() => {
    assuranceIdRef.current = '';
    void message.success(t(`${TRANSLATION}.success-banner`));
    navigateKeepSearch(RootRoutes.SELF_SERVICE);
  }, [navigateKeepSearch, t]);

  const renderStep = () => {
    switch (currentStep) {
      case EpcsSetupStep.STEP1_START:
        return (
          <EpcsStep1Start
            authenticatorId={authenticatorId}
            tenantId={tenantId}
            workflowId={workflowIdRef.current}
            assuranceIdRef={assuranceIdRef}
            setStep={setCurrentStep}
            cancelFlow={cancelFlow}
          />
        );
      case EpcsSetupStep.STEP2_VERIFY_IDENTITY:
        return (
          <EpcsStep2VerifyIdentity
            assuranceId={assuranceIdRef.current}
            tenantId={tenantId}
            workflowId={workflowIdRef.current}
            setStep={setCurrentStep}
            cancelFlow={cancelFlow}
          />
        );
      case EpcsSetupStep.STEP3_VERIFY_AUTHENTICATOR:
        return (
          <EpcsStep3VerifyAuthenticator
            assuranceId={assuranceIdRef.current}
            authenticatorId={authenticatorId}
            authenticatorShortName={authenticatorShortName}
            authenticatorDeviceModel={authenticatorDeviceModel}
            tenantId={tenantId}
            workflowId={workflowIdRef.current}
            setPKCECodes={setPKCECodes}
            setStep={setCurrentStep}
            cancelFlow={cancelFlow}
          />
        );
      case EpcsSetupStep.STEP4_SUPERVISOR_AUTHENTICATE:
        return (
          <EpcsStep4SupervisorAuthenticate
            assuranceId={assuranceIdRef.current}
            pkceCodes={pkceCodes}
            tenantId={tenantId}
            workflowId={workflowIdRef.current}
            cancelFlow={cancelFlow}
            completeFlow={completeFlow}
          />
        );
      default:
        cancelFlow();
        return undefined;
    }
  };

  const graphicSrc = (() => {
    switch (currentStep) {
      case EpcsSetupStep.STEP2_VERIFY_IDENTITY:
        return EpcsVerifyIdImg;
      case EpcsSetupStep.STEP3_VERIFY_AUTHENTICATOR:
        return EpcsVerifyIidImg;
      default:
        return EpcsStartImg;
    }
  })();

  return (
    <div className="epcs-setup">
      {currentStep === EpcsSetupStep.STEP4_SUPERVISOR_AUTHENTICATE ? (
        renderStep()
      ) : (
        <AuthMethodLayout
          graphic={
            <>
              <img src={graphicSrc} alt="Desktop" className="left-panel-image-desktop" />
              <img src={graphicSrc} alt="Mobile" className="left-panel-image-mobile" />
            </>
          }
        >
          {renderStep()}
        </AuthMethodLayout>
      )}
    </div>
  );
};

export default EpcsSetup;
